// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Estoque/MovimentacaoEstoque.tsx
//  Listagem paginada de movimentações de estoque
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Eye, ArrowDownCircle, ArrowUpCircle, RefreshCw,
         TrendingDown, TrendingUp, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useMovimentacao, useTiposMovimento } from '@/features/estoque/hooks/useMovimentacao';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { Input } from '@/components/ui/input';
import { TIPOS_MOVIMENTO } from '@/features/estoque/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { LocalEstoqueSelect } from '@/components/LocalEstoqueSelect';
import { UsuarioNome } from '@/components/UsuarioNome';

const PAGE_SIZES = [10, 20, 50] as const;

const tipoConfig: Record<number, { className: string; icon: typeof ArrowDownCircle }> = {
  1: { className: 'bg-green-500/10 text-green-600',  icon: ArrowDownCircle  },
  2: { className: 'bg-red-500/10 text-red-600',      icon: ArrowUpCircle    },
  3: { className: 'bg-blue-500/10 text-blue-600',    icon: RefreshCw        },
  4: { className: 'bg-emerald-500/10 text-emerald-600', icon: TrendingUp    },
  5: { className: 'bg-orange-500/10 text-orange-600',   icon: TrendingDown  },
};

const MovimentacaoEstoque = () => {
  const navigate = useNavigate();
  const [tipoFiltro,     setTipoFiltro]     = useState(0);
  const [pagina,         setPagina]         = useState(1);
  const [tamanhoPagina,  setTamanhoPagina]  = useState(20);
  const [deletingId,     setDeletingId]     = useState<number | null>(null);
  const [busca,          setBusca]          = useState('');
  const [produtoId,      setProdutoId]      = useState<number | undefined>();
  const [localId,        setLocalId]        = useState<number | undefined>();
  // Busca, período, produto/local (FrmFiltroMovimentoEstoque) e ordem vão ao servidor: o histórico cresce sem limite.
  const buscaEnviada = useDebounce(busca.trim(), 400);
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'data' | 'id' | 'valor'>('data', true, () => setPagina(1));

  const { movimentacoes, total, totalPaginas, isLoading, isFetching, deleteMovimentacao } =
    useMovimentacao({
      pagina, tamanhoPagina, tipoId: tipoFiltro,
      filtros: {
        busca: buscaEnviada, ...periodo.intervalo,
        produtoId, localId,
        ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
      },
    });

  const { data: tipos } = useTiposMovimento();

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPagina(1); };
  const handleTipoChange     = (value: string) => { setTipoFiltro(Number(value)); setPagina(1); };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Movimentação de Estoque</h1>
          <p className="text-muted-foreground mt-1">Registros de entradas, saídas, transferências e ajustes</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/estoque/movimentacoes/nova')}>
          <Plus className="h-4 w-4" />Nova Movimentação
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <CardTitle className="whitespace-nowrap">Histórico de Movimentações</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-60">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Documento ou justificativa..."
                value={busca}
                onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
                className="pl-9"
              />
            </div>
            <PeriodoFiltro estado={periodo} rotuloData="Data do movimento" />
            <Select value={String(tipoFiltro)} onValueChange={handleTipoChange}>
              <SelectTrigger className="w-full sm:w-52">
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Todos os tipos</SelectItem>
                {(tipos ?? TIPOS_MOVIMENTO).map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>{t.descricao}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3 pt-3">
            <div className="space-y-1 w-72">
              <Label className="text-xs text-muted-foreground">Produto</Label>
              <div className="flex gap-1">
                <ProdutoSearchCombobox value={produtoId} onChange={(id) => { setProdutoId(id || undefined); setPagina(1); }} placeholder="Todos os produtos" />
                {produtoId && <Button variant="ghost" size="sm" onClick={() => { setProdutoId(undefined); setPagina(1); }}>Limpar</Button>}
              </div>
            </div>
            <div className="space-y-1 w-52">
              <Label className="text-xs text-muted-foreground">Local de estoque</Label>
              <LocalEstoqueSelect value={localId} onChange={(id) => { setLocalId(id); setPagina(1); }} rotuloTodos="Todos os locais" />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="data" ordenacao={ordenacao}>Data</CabecalhoOrdenavel>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Nº Documento</TableHead>
                  <TableHead>Justificativa</TableHead>
                  <TableHead>Usuário</TableHead>
                  <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Total Movimentado</CabecalhoOrdenavel>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : movimentacoes.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhuma movimentação neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma movimentação encontrada'}
                  </TableCell></TableRow>
                ) : (
                  movimentacoes.map((m) => {
                    const cfg  = tipoConfig[m.tipoMovimentoId] ?? { className: 'bg-gray-500/10 text-gray-600', icon: RefreshCw };
                    const Icon = cfg.icon;
                    return (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">{new Date(m.dtMovimento).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1">
                              <Icon className="h-3 w-3" />{m.tipoMovimentoDesc}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell>{m.numDocumento || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{m.justificativa || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-sm"><UsuarioNome id={m.usuarioId} /></TableCell>
                        <TableCell className="text-right font-medium">
                          {m.totalMovimentado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/estoque/movimentacoes/${m.id}`)} title="Ver detalhe">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeletingId(m.id)} title="Excluir">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteMovimentacao(deletingId); setDeletingId(null); } }}
        title="Excluir Movimentação"
        description="Esta ação é irreversível. Deseja excluir esta movimentação de estoque?"
      />
    </div>
  );
};

export default MovimentacaoEstoque;
